import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { permissionsDictionary, permissionsDictionaryTranslate } from './models/permissions';
import { ProfilesService } from '../../services/profiles/profiles.service';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-manage-permissions',
  templateUrl: './manage-permissions.component.html',
  styleUrls: ['./manage-permissions.component.sass'],
})
export class ManagePermissionsComponent implements OnInit {
  permissions: any = permissionsDictionary;
  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private profileService: ProfilesService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ManagePermissionsComponent>
  ) {}

  ngOnInit(): void {
    window.scrollTo(0, 0);
    this.form = this.fb.group({
      name: [this.data?.name || '', Validators.required],
      role: [this.processProfileType(this.data?.roles) || '', Validators.required],
      permissions: this.fb.array(
        this.permissions?.operations
          .concat(this.permissions?.reports)
          .map((op: any) => this.createPermissionGroup(op))
      ),
    });

    this.profileService.listPermissionRoles().subscribe((response) => {
      this.data.roles = response;
    });
  }

  createPermissionGroup(op: any): FormGroup {
    return this.fb.group({
      name: [op.value],
      value: [this.isPermissionChecked(op.value)],
    });
  }

  onSave(): void {
    if (!this.form.valid) {
      this.toastr.warning('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    const information: any = {
      name: this.form.value.name,
      rolesId: this.treatmentRolesUpdate(this.form.value.role),
      attributes: this.form.value.permissions,
    };

    const isEdit = Boolean(this.data?.id);
    let saveOperation: Observable<any>;

    if (isEdit) {
      information.id = this.data.id;
      saveOperation = this.profileService.updatePermissions(information);
    } else {
      saveOperation = this.profileService.registerPermission(information);
    }

    saveOperation.subscribe({
      next: () => {
        const successMessage = isEdit
          ? 'Perfil atualizado com sucesso.'
          : 'Perfil criado com sucesso.';
        this.toastr.success(successMessage);
        this.dialogRef.close({ up: true });
      },
      error: (error) => {
        const errorMessage = error?.errors?.join(', ') || 'Erro ao processar a solicitação.';
        this.toastr.error(errorMessage);
      },
    });
  }

  treatmentRolesUpdate(role: string): string[] {
    const roleMapping = {
      'IPS': ['IPS'],
      'TVF': ['TVF'],
      'TVO': ['TVO'],
      'IPS_TVF': ['IPS', 'TVF'],
      'IPS_TVO': ['IPS', 'TVO'],
      'TVF_TVO': ['TVF', 'TVO'],
      'IPS_TVF_TVO': ['IPS', 'TVF', 'TVO']
    };

    const moduleNames = roleMapping[role] || [];
    return this.getRoleIdsByNames(moduleNames);
  }

  private getRoleIdsByNames(moduleNames: string[]): string[] {
    return moduleNames
      .map(name => this.data.roles?.find(role => role.name === name))
      .filter(role => role !== undefined)
      .map(role => role.id);
  }

  processProfileType(profileTypes: { id: string; name: string }[]): string {
    if (!profileTypes?.length) return '';

    const activeModules = this.getActiveModules(profileTypes);
    return this.determineProfileType(activeModules);
  }

  private getActiveModules(profileTypes: { id: string; name: string }[]): string[] {
    return profileTypes
      .map(type => type.name)
      .filter(name => ['IPS', 'TVF', 'TVO'].includes(name));
  }

  private determineProfileType(activeModules: string[]): string {
    const hasIPS = activeModules.includes('IPS');
    const hasTVF = activeModules.includes('TVF');
    const hasTVO = activeModules.includes('TVO');

    const profileTypeMap = {
      'IPS_TVF_TVO': hasIPS && hasTVF && hasTVO,
      'IPS_TVF': hasIPS && hasTVF && !hasTVO,
      'IPS_TVO': hasIPS && !hasTVF && hasTVO,
      'TVF_TVO': !hasIPS && hasTVF && hasTVO,
      'TVF': !hasIPS && hasTVF && !hasTVO,
      'IPS': hasIPS && !hasTVF && !hasTVO,
      'TVO': !hasIPS && !hasTVF && hasTVO
    };


    const conditionPermission = Object.keys(profileTypeMap).find(key => profileTypeMap[key]) || ''

    return conditionPermission;
  }

  isPermissionChecked(title: string): boolean {
    const matchingAttribute = this.data?.attributes?.find(
      (attr: { name: string; value: boolean }) => attr.name === title
    );

    if (!matchingAttribute) {
      return false;
    }

    return Boolean(matchingAttribute.value);
  }

  handleCheckboxClick(title: string, checkbox: boolean): void {
    const permissionsArray = this.form.get('permissions') as FormArray;

    permissionsArray.controls.forEach((control: FormGroup) => {
      const name = control.get('name').value;

      if (name === title) {
        control.get('value').setValue(checkbox);
        this.isPermissionChecked(title);
      }
    });
  }
}
